import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { NavigationStart, Router, Event } from "@angular/router";
import { Subscription } from "rxjs";
import { SelectDealerComponent } from "src/components/layout/select-dealer/select-dealer.component";
import { MenuLinkViewModel } from "src/domain/client";
import { ChannelRequestService } from "src/services/channel-request.service";
import { ModelAndDealerService } from "src/services/model-and-dealer.service";
import { NavigationService } from "src/services/navigation.service";
import { ShoppingCartService } from "src/services/shopping-cart.service";
import { ThemeService } from "src/services/theme.service";
import { ExpandComponent } from "../expand/expand.component";
import { ErrorReporterService } from "src/services/error-reporter.service";
import { ViewportScroller } from "@angular/common";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "vw-navigation",
  templateUrl: "./navigation.component.html",
})
export class NavigationComponent implements OnInit, OnDestroy {
  @ViewChild("navbar") navbar: ElementRef<HTMLElement> | null = null;
  @ViewChild(ExpandComponent) shoppingCartExpand?: ExpandComponent;
  @ViewChild(SelectDealerComponent) selectDealer?: SelectDealerComponent;

  alwaysFillNav = true;

  private openSelectDealerSubscription?: Subscription;
  private routerEventsSubscription?: Subscription;

  channelRequestSubscription = new Subscription();

  constructor(
    public channelRequestService: ChannelRequestService,
    public modelAndDealerService: ModelAndDealerService,
    public shoppingCartService: ShoppingCartService,
    public themeService: ThemeService,
    private navigationService: NavigationService,
    private errorReporterService: ErrorReporterService,
    private router: Router,
    private viewportScroller: ViewportScroller,
    private translateService: TranslateService,
  ) {
    this.routerEventsSubscription = this.router.events.subscribe(
      (event: Event) => {
        if (event instanceof NavigationStart) {
          this.setAlwaysFillNav(event.url);
          this.active = undefined;
          this.navbarCollapsed = true;
        }
      },
    );
    this.viewportScroller.setOffset(() => {
      const navEl = this.navbar?.nativeElement;
      if (!navEl) return [0, 0];
      const { height } = navEl.getBoundingClientRect();
      return [0, Math.ceil(height)];
    });
  }

  navbarCollapsed = true;
  fillNav = false;
  hovering = false;
  menuLinks: MenuLinkViewModel[] = [];
  active: undefined | string = undefined;

  @HostListener("window:scroll", ["$event"])
  onScroll(): void {
    this.fillNav = window.scrollY > 0;
  }

  ngOnInit(): void {
    this.setAlwaysFillNav(this.router.url);
    this.getMenu();

    this.openSelectDealerSubscription =
      this.modelAndDealerService.openSelectDealer$.subscribe(() =>
        this.openSelectDealer(),
      );

    this.channelRequestSubscription =
      this.channelRequestService.channelRequestUpdated$.subscribe(() =>
        this.getMenu(),
      );
  }

  get isCarSelected() {
    return !!this.channelRequestService.channelRequest?.shoppingCart?.vehicle;
  }

  get isDealerSelected() {
    return !!this.modelAndDealerService?.selectedDealer;
  }

  get selectedCarOrDealerLabel() {
    if (this.isCarSelected && this.isDealerSelected) {
      return this.translateService.instant("navigation.selectedRegNrAndDealer");
    }
    if (this.isCarSelected) {
      return this.translateService.instant("navigation.selectedRegNr");
    }
    if (this.isDealerSelected) {
      return this.translateService.instant("navigation.selectedDealer");
    }
  }

  ngOnDestroy(): void {
    this.openSelectDealerSubscription?.unsubscribe();
    this.routerEventsSubscription?.unsubscribe();
    this.channelRequestSubscription.unsubscribe();
    this.viewportScroller.setOffset([0, 0]);
  }

  toggleDropdown(event: MouseEvent, dropdown: string): void {
    event.stopPropagation();
    this.active = dropdown;
  }

  toggleNavbar(): void {
    this.active = undefined;
    this.navbarCollapsed = !this.navbarCollapsed;
  }

  openSelectDealer(event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
    }

    this.active = undefined;
    this.navbarCollapsed = true;

    this.selectDealer?.open();
  }

  openShoppingCart(event: MouseEvent): void {
    event.preventDefault();
    this.shoppingCartExpand?.toggleOpen();
  }

  clearShoppingCart(): void {
    this.shoppingCartService.clearShoppingCart();
  }

  goToCheckout(): void {
    this.router.navigate([this.themeService.brand, "checkout"]);
    this.shoppingCartExpand?.toggleOpen();
  }

  mouseEnter(): void {
    this.hovering = true;
  }

  mouseLeave(): void {
    this.hovering = false;
  }

  //////////

  private getMenu(): void {
    this.navigationService
      .getMenu()
      .then((menuLinks) => (this.menuLinks = menuLinks))
      .catch(this.errorReporterService.handle);
  }

  private setAlwaysFillNav(url: string): void {
    this.alwaysFillNav = url.split("/").length - 1 > 1;
  }
}
